import type { AddToPurchaseListStrategy } from '@/components/purchase-order/AddToPurchaseListDialog';
import {
  buildInitialGroupsFromSelected,
  buildProductPlaceholderGroups,
  hasFullSuggestionCoverage,
  mergeProductExpansion,
  mergeSuggestions,
} from '@/components/purchase-order/AddToPurchaseListDialog';
import { fetchExpansionRows } from '@/utils/purchase-order/expansionReportFetcher';
import { fetchVariantsByProductNames } from '@/utils/purchase-order/fetchVariantsByProductNames';
import { distinctProductNames } from '@/utils/purchase-order/reportRowToExpansionFilter';
import type { FilterColumnList } from '@/collections/filter_columns';

export interface ReportStrategyInput {
  selectedRows: any[];
  dimensionCode: 'by_product' | 'by_variant' | string;
  sourceReport: {
    filterColumns?: FilterColumnList | null;
    filterTimerange?: string[] | null;
    reportParams?: any;
    productSegment?: any;
  };
}

export function reportStrategy(input: ReportStrategyInput): AddToPurchaseListStrategy {
  const isByVariant = input.dimensionCode === 'by_variant';
  const productNames = distinctProductNames(input.selectedRows);

  if (isByVariant) {
    const initialGroups = buildInitialGroupsFromSelected(input.selectedRows);
    const enrich = async () => {
      const { rows } = await fetchExpansionRows({ productNames, sourceReport: input.sourceReport });
      return rows;
    };
    // Case A: every selected row already carries [inventory_replenishment].
    // Render straight from row data — no open-time fetch. The enrich
    // function is still wired up so submit() can lazily run it if any
    // variant turns out to have an unresolvable variant_id (archived /
    // inventory-not-tracked products). For the common case where every
    // row has a finite variant_id, submit's lazy check short-circuits
    // and no network call ever fires.
    if (hasFullSuggestionCoverage(input.selectedRows)) {
      return {
        initialGroups,
        enrich,
        mergeEnrichment: mergeSuggestions,
        enrichmentRequired: false,
        enrichAtSubmit: true,
      };
    }
    return {
      initialGroups,
      enrich,
      mergeEnrichment: mergeSuggestions,
      enrichmentRequired: false,
    };
  }

  // by_product, two-phase (AS-10567).
  //
  // Phase 1 asks the master-data cube for variant identity only. It carries
  // no forecast computation, so the dialog can render checkable rows with qty
  // inputs quickly. Phase 2 runs the original replenishment expansion behind
  // it and fills the Suggested column in when it lands — the dialog never
  // blocks the user on it.
  //
  // Both phases key on the same variant_id space, so mergeSuggestions matches
  // phase-2 rows onto the variants phase 1 rendered.
  return {
    initialGroups: buildProductPlaceholderGroups(input.selectedRows),
    enrich: async () => fetchVariantsByProductNames(productNames),
    mergeEnrichment: mergeProductExpansion,
    enrichSuggestions: async () => {
      const { rows } = await fetchExpansionRows({ productNames, sourceReport: input.sourceReport });
      return rows;
    },
    mergeSuggestionEnrichment: mergeSuggestions,
    enrichmentRequired: true,
  };
}
